import { CheckCircle2, CircleAlert, RefreshCw, Send } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";

import { useCardholderSend } from "../hooks/useCardholderSend";
import type { CardholderBoardItem, CardholderSkipReason } from "../types/cardholderOnboarding.types";
import { SKIP_REASON_LABEL_KEYS } from "../utils/cardholderBoard";

export interface SendToProviderDialogProps {
  organizationId: string;
  items: CardholderBoardItem[];
  providerLabel: string;
  onClose: () => void;
  /** Called once the run went out, so the board reads the new state. */
  onSent: () => void;
}

/** Dry run of the chosen cardholders → review who goes and who does not → the real run of those who go. */
export function SendToProviderDialog({
  organizationId,
  items,
  providerLabel,
  onClose,
  onSent,
}: SendToProviderDialogProps) {
  const { t } = useTranslations("cardholderOnboarding");
  const send = useCardholderSend(
    organizationId,
    items.map((item) => item.identity_id),
    t("error_generic"),
  );
  const nameOf = new Map(items.map((item) => [item.identity_id, item.name ?? t("unnamed")]));
  const reported = new Set(send.preview?.results.map((row) => row.identity_id) ?? []);
  // The run only reports active people; a row whose status or type changed since the board was read is absent.
  const skipped = send.preview
    ? [
        ...send.preview.results.filter((row) => row.outcome === "skipped"),
        ...items
          .filter((item) => !reported.has(item.identity_id))
          .map((item) => ({ identity_id: item.identity_id, reason: "changed" as const })),
      ]
    : [];
  const pausedAccepted = items.filter(
    (item) => item.bucket === "paused" && send.accepted.includes(item.identity_id),
  ).length;
  const busy = send.phase === "checking" || send.phase === "sending";

  const handleSend = async () => {
    if (await send.send()) onSent();
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title={t("send_title", { provider: providerLabel })}
      description={t("send_description", { provider: providerLabel })}
      footer={
        send.phase === "done" ? (
          <ColocaButton variant="primary" onClick={onClose}>
            {t("close")}
          </ColocaButton>
        ) : send.phase === "error" ? (
          <>
            <ColocaButton variant="ghost" onClick={onClose}>
              {t("close")}
            </ColocaButton>
            <ColocaButton variant="primary" icon={RefreshCw} onClick={() => void send.check()}>
              {t("send_recheck")}
            </ColocaButton>
          </>
        ) : (
          <>
            <ColocaButton variant="ghost" disabled={busy} onClick={onClose}>
              {t("cancel")}
            </ColocaButton>
            <ColocaButton
              variant="primary"
              icon={Send}
              loading={send.phase === "sending"}
              disabled={send.phase !== "review" || send.accepted.length === 0}
              onClick={() => void handleSend()}
            >
              {t("send_confirm", { count: send.accepted.length })}
            </ColocaButton>
          </>
        )
      }
    >
      {send.phase === "checking" && (
        <div className="space-y-2" aria-busy>
          <p className="text-sm text-muted-foreground">{t("send_checking")}</p>
          <ColocaSkeleton className="h-16 w-full" />
        </div>
      )}

      {send.phase === "error" && send.errorMessage && (
        <ColocaAlert variant="destructive" icon={CircleAlert} text={send.errorMessage} />
      )}

      {(send.phase === "review" || send.phase === "sending") && send.preview && (
        <div className="space-y-4">
          {send.accepted.length > 0 ? (
            <p className="text-sm">
              <span className="font-medium">{t("send_ready", { count: send.accepted.length })}</span>{" "}
              <span className="text-muted-foreground">
                {t("send_queue_note", { provider: providerLabel })}
              </span>
            </p>
          ) : (
            <ColocaAlert variant="warning" icon={CircleAlert} text={t("send_none")} />
          )}

          {pausedAccepted > 0 && (
            <ColocaAlert
              variant="secondary"
              icon={CircleAlert}
              text={t("send_paused_note", { count: pausedAccepted })}
            />
          )}

          {skipped.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("send_skipped_heading", { count: skipped.length })}</p>
              <ul className="max-h-[220px] divide-y divide-border overflow-y-auto rounded-xl border border-border">
                {skipped.map((row) => (
                  <li key={row.identity_id} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:gap-3">
                    <span className="truncate text-sm font-medium sm:w-48 sm:shrink-0">
                      {nameOf.get(row.identity_id) ?? row.identity_id}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {row.reason === "changed"
                        ? t("skip_changed")
                        : row.reason
                          ? t(SKIP_REASON_LABEL_KEYS[row.reason as CardholderSkipReason])
                          : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {send.phase === "done" && send.result && (
        <div className="space-y-3">
          <ColocaAlert
            variant="success"
            icon={CheckCircle2}
            heading={t("send_done_heading")}
            text={t("send_done_counts", {
              queued: send.result.counts.queued,
              resumed: send.result.counts.resumed,
            })}
          />
          {send.result.counts.skipped > 0 && (
            <p className="text-sm text-muted-foreground">
              {t("send_done_skipped", { count: send.result.counts.skipped })}
            </p>
          )}
          <p className="text-sm text-muted-foreground">{t("send_done_note", { provider: providerLabel })}</p>
        </div>
      )}
    </ColocaDialog>
  );
}
